import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup';
import { Logo } from '../../components/images';
import { AuthData } from '../../provider/AuthProvider';
import { FaEye, FaEyeSlash, FaEnvelope, FaCheckCircle } from 'react-icons/fa';

const SignUp = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const navigate = useNavigate()
    const { register } = AuthData()
    const [showPassword, setShowPassword] = useState(false);
    
    const handleRegister = async (values, {setErrors, setSubmitting}) => {
        try{
            // Clean and prepare data before sending to register function
            const cleanData = {
                firstName: String(values.firstName || '').trim(),
                lastName: String(values.lastName || '').trim(),
                email: String(values.email || '').trim().toLowerCase(),
                password: String(values.password || '').trim(),
                phone: String(values.phone || '').trim(),
                location: String(values.location || '').trim()
            };

            console.log('🚀 Registration data:', cleanData);
            
            // Register user with email verification
            await register(cleanData);
            console.log('✅ Registration successful');
            
            // Navigate to success page with email
            navigate('/registration-success', { 
                state: { email: cleanData.email } 
            });
            
        }catch(error){
            console.error('❌ Registration error:', error);
            setErrors({general: error.message});
        }finally{
            setSubmitting(false);
        }
    }

    return (
        <div className='bg-gradient-to-br from-orange-200 to-orange-500 min-h-screen w-full p-4 sm:p-6 lg:p-10'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8'>
                <Link to="/main-signup">
                    <div className='flex flex-row items-center mb-4 sm:mb-0'>
                        <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
                        <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
                    </div>
                </Link>
                
                <div className='flex sm:flex-row justify-center gap-2 sm:gap-3'>               
                    <Link to='/about'                             
                    className='text-md sm:text-md hover:underline'               
                    >                 
                        About               
                    </Link>               
                    <Link to='/faq'                              
                    className='text-md sm:text-md hover:underline'               
                    >                 
                        FAQs               
                    </Link>             
                </div>
            </div>
            <div className="flex justify-center items-center">
                <div className='flex flex-col gap-6 p-8 md:p-12 bg-white shadow-2xl max-w-xl w-full rounded-xl'>
                    <div className='text-center'>
                        <h2 className='text-2xl font-bold text-orange-400 mb-2'>Create Your Account</h2>
                        <p className='text-gray-600'>Join thousands of students finding their dream internships</p>
                    </div>

                    <Formik
                        initialValues={{
                            firstName: '',
                            lastName: '',
                            email: '',
                            password: '',
                            confirmPassword: '',
                            phone: '',
                            location: ''
                        }}
                        validationSchema={Yup.object({
                            firstName: Yup.string()
                                .min(2, 'First name must be at least 2 characters')
                                .max(50, 'First name must be less than 50 characters')
                                .required('First name is required'),
                            lastName: Yup.string()
                                .min(2, 'Last name must be at least 2 characters')
                                .max(50, 'Last name must be less than 50 characters')
                                .required('Last name is required'),
                            email: Yup.string()
                                .matches(emailRegex, 'Please enter a valid email address')
                                .required('Email is required'),
                            password: Yup.string()
                                .min(6, 'Password must be at least 6 characters')
                                .required('Password is required'),
                            confirmPassword: Yup.string()
                                .oneOf([Yup.ref('password'), null], 'Passwords must match')
                                .required('Please confirm your password'),
                            phone: Yup.string()
                                .min(10, 'Phone number must be at least 10 digits')
                                .required('Phone number is required'),
                            location: Yup.string()
                                .min(2, 'Location must be at least 2 characters')
                                .required('Location is required')
                        })}
                        onSubmit={handleRegister}
                    >
                        {({ isSubmitting, errors }) => (
                            <Form className='space-y-4'>
                                {/* General Error Message */}
                                {errors.general && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                        {errors.general}
                                    </div>
                                )}

                                {/* Name Fields */}
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                                            First Name *
                                        </label>
                                        <Field
                                            name="firstName"
                                            type="text"
                                            placeholder="Enter your first name"
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                        />
                                        <ErrorMessage name="firstName" component="div" className="text-red-600 text-sm mt-1" />
                                    </div>

                                    <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                                            Last Name *
                                        </label>
                                        <Field
                                            name="lastName"
                                            type="text"
                                            placeholder="Enter your last name"
                                            className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                        />
                                        <ErrorMessage name="lastName" component="div" className="text-red-600 text-sm mt-1" />
                                    </div>
                                </div>

                                {/* Email */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                        Email Address *
                                    </label>
                                    <Field
                                        name="email"
                                        type="email"
                                        placeholder="Enter your email address"
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                    />
                                    <ErrorMessage name="email" component="div" className="text-red-600 text-sm mt-1" />
                                </div>

                                {/* Password Fields */}
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                    <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                                            Password *
                                        </label>
                                        <div className='relative'>
                                            <Field
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Create a password"
                                                className='w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700'
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        <ErrorMessage name="password" component="div" className="text-red-600 text-sm mt-1" />
                                    </div>

                                    <div>
                                        <label className='block text-sm font-medium text-gray-700 mb-1'>
                                            Confirm Password *
                                        </label>
                                        <div className='relative'>
                                            <Field
                                                name="confirmPassword"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Confirm your password"
                                                className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700'
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        <ErrorMessage name="confirmPassword" component="div" className="text-red-600 text-sm mt-1" />
                                    </div>
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                        Phone Number *
                                    </label>
                                    <Field
                                        name="phone"
                                        type="tel"
                                        placeholder="Enter your phone number"
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                    />
                                    <ErrorMessage name="phone" component="div" className="text-red-600 text-sm mt-1" />
                                </div>

                                {/* Location */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                                        Location *
                                    </label>
                                    <Field
                                        name="location"
                                        type="text"
                                        placeholder="Enter your city/location"
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                    />
                                    <ErrorMessage name="location" component="div" className="text-red-600 text-sm mt-1" />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className='w-full bg-orange-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-orange-600 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                                >
                                    {isSubmitting ? 'Creating Account...' : 'Create Account'}
                                </button>

                                {/* Login Link */}
                                <div className='text-center pt-4'>
                                    <p className='text-gray-600'>
                                        Already have an account?{' '}
                                        <Link to="/login" className='text-orange-500 font-medium hover:text-orange-600'>
                                            Sign In
                                        </Link>
                                    </p>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    )
}

export default SignUp